import React from 'react';

import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import NavigationHeader from '../homepage/components/NavigationHeader';
import Button from '../../components/ui/Button';
import StatCard from '../biodiversity-status-center/components/StatCard';
import TurtleHatchlingTable from '../biodiversity-status-center/components/TurtleHatchlingTable';
import TurtleNestingTable from '../biodiversity-status-center/components/TurtleNestingTable';

const MarineTurtleConservation = () => {
  const statsData = [
  {
    icon: 'Waves',
    iconBg: 'bg-info/20',
    iconColor: 'text-info',
    value: '19,103',
    label: 'Total Hatchlings Released',
    subtitle: '2014-2024 conservation efforts',
    change: 162.5
  },
  {
    icon: 'MapPin',
    iconBg: 'bg-success/20',
    iconColor: 'text-success',
    value: '8',
    label: 'Protected Nesting Sites',
    subtitle: 'Active monitoring zones'
  },
  {
    icon: 'Activity',
    iconBg: 'bg-secondary/20',
    iconColor: 'text-secondary',
    value: '337',
    label: 'Nesting Incidents Recorded',
    subtitle: 'Over 11-year monitoring period'
  },
  {
    icon: 'Users',
    iconBg: 'bg-primary/20',
    iconColor: 'text-primary',
    value: '450+',
    label: 'Community Volunteers',
    subtitle: 'Supporting conservation efforts'
  }];


  const turtleSpecies = [
  {
    name: 'Green Sea Turtle',
    scientificName: 'Chelonia mydas',
    image: "https://images.unsplash.com/photo-1655365225159-e6de86b2fefa",
    imageAlt: 'Green sea turtle swimming gracefully through crystal clear turquoise ocean waters with sunlight filtering from above',
    status: 'Endangered',
    description: 'Named for the greenish color of their cartilage and fat, Green Sea Turtles are the largest hard-shelled sea turtles. Adults can weigh up to 190 kg and feed primarily on seagrass and algae. They play a crucial role in maintaining healthy seagrass beds and coral reef ecosystems.'
  },
  {
    name: 'Hawksbill Turtle',
    scientificName: 'Eretmochelys imbricata',
    image: "https://images.unsplash.com/photo-1511168836158-bb7074a169eb",
    imageAlt: 'Hawksbill sea turtle with distinctive patterned shell swimming near vibrant coral reef in tropical waters',
    status: 'Critically Endangered',
    description: 'Recognizable by their narrow, pointed beak and beautiful patterned shells, Hawksbill Turtles are critically endangered. They feed primarily on sponges and play a vital role in reef health. Their shells have historically been used for tortoiseshell products, leading to severe population declines.'
  },
  {
    name: 'Olive Ridley Turtle',
    scientificName: 'Lepidochelys olivacea',
    image: "https://images.unsplash.com/photo-1617818866012-242d1f3b7359",
    imageAlt: 'Olive Ridley sea turtle with olive-colored heart-shaped shell resting on sandy beach during nesting season',
    status: 'Vulnerable',
    description: 'The smallest of the sea turtles, Olive Ridleys are known for their synchronized mass nesting events called "arribadas." They have an olive-colored heart-shaped shell and are omnivorous feeders. Despite being the most abundant sea turtle, they face threats from fishing bycatch and habitat loss.'
  }];


  const conservationPrograms = [
  {
    icon: 'Shield',
    title: 'Beach Patrol & Nest Protection',
    image: 'https://images.unsplash.com/photo-1619341374997-13a03b09cf07',
    imageAlt: 'Stunning white sand beach with crystal-clear turquoise waters and dramatic limestone karst formations representing protected nesting sites for marine turtles',
    description: 'Round-the-clock monitoring of nesting beaches during peak season. Rangers and volunteers protect nests from predators, poachers, and environmental threats. Relocated nests when necessary to ensure maximum hatchling survival rates.',
    impact: '85% nest protection success rate'
  },
  {
    icon: 'Heart',
    title: 'Hatchling Release Program',
    image: 'https://images.unsplash.com/photo-1617818866012-242d1f3b7359',
    imageAlt: 'Olive Ridley sea turtle with olive-colored heart-shaped shell resting on sandy beach during nesting season',
    description: 'Carefully timed releases of hatchlings to maximize survival chances. Community involvement in release events raises awareness and builds local stewardship. Monitoring programs track hatchling success and beach conditions.',
    impact: '19,103 hatchlings released since 2014'
  },
  {
    icon: 'Users',
    title: 'Community Education & Engagement',
    image: 'https://images.unsplash.com/photo-1702477347184-5f36f44de2a4',
    imageAlt: 'Traditional wooden boats operated by local community members navigating through pristine mangrove-lined river channels, showcasing sustainable community-based tourism and conservation partnership',
    description: 'Educational programs for local communities, schools, and tourists about marine turtle conservation. Training programs for community volunteers and eco-tourism guides. Livelihood alternatives for former turtle egg collectors.',
    impact: '450+ active community volunteers'
  },
  {
    icon: 'Search',
    title: 'Research & Monitoring',
    image: 'https://images.unsplash.com/photo-1508797033483-cc40d4225773',
    imageAlt: 'Eco-friendly wooden jungle boardwalk trail winding through pristine tropical rainforest with lush green vegetation, sustainable tourism infrastructure at PPSRNP',
    description: 'Scientific research on nesting patterns, population dynamics, and migration routes. Tagging programs to track individual turtles and understand their life cycles. Data collection to inform conservation strategies and policy decisions.',
    impact: '11 years of comprehensive data'
  }];


  const threats = [
  {
    icon: 'AlertTriangle',
    title: 'Plastic Pollution',
    description: 'Marine debris and plastic waste pose ingestion and entanglement risks'
  },
  {
    icon: 'Anchor',
    title: 'Fishing Bycatch',
    description: 'Accidental capture in fishing nets and gear causes injury and mortality'
  },
  {
    icon: 'Home',
    title: 'Habitat Loss',
    description: 'Coastal development and beach erosion reduce available nesting sites'
  },
  {
    icon: 'Sun',
    title: 'Climate Change',
    description: 'Rising temperatures affect sand temperature and sex ratios of hatchlings'
  }];


  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <NavigationHeader />
      
      <div className="pt-20">
        {/* Hero Section */}
        <div className="relative h-[400px] overflow-hidden">
          <Image
            src="https://img.rocket.new/generatedImages/rocket_gen_img_12903615e-1765151405540.png"
            alt="Marine turtle swimming in crystal clear ocean waters with coral reef visible below, showcasing the pristine marine ecosystem of Puerto Princesa Subterranean River National Park"
            className="w-full h-full object-cover" />

          <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-background)] via-[var(--color-background)]/50 to-transparent" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-4">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 drop-shadow-lg">
                Marine Turtle Conservation
              </h1>
              <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto drop-shadow-md">
                Protecting endangered sea turtles through community-driven conservation programs
              </p>
            </div>
          </div>
        </div>

        {/* Statistics Section */}
        <section className="py-12 bg-[var(--color-background)]">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {statsData?.map((stat, index) =>
              <StatCard key={index} stat={stat} />
              )}
            </div>
          </div>
        </section>

        {/* Turtle Species Section */}
        <section className="py-12 bg-[var(--color-background)]">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-foreground)] mb-4">
                Protected Marine Turtle Species
              </h2>
              <p className="text-lg text-[var(--color-muted-foreground)] max-w-3xl mx-auto">
                Three species of endangered sea turtles nest along our protected coastlines.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {turtleSpecies?.map((species, index) =>
              <div key={index} className="bg-[var(--color-card)] rounded-lg overflow-hidden shadow-conservation hover:shadow-conservation-lg animation-organic border border-[var(--color-border)]">
                  <div className="relative h-64 overflow-hidden">
                    <Image
                    src={species?.image}
                    alt={species?.imageAlt}
                    className="w-full h-full object-cover" />
                    <div className="absolute top-3 right-3 px-3 py-1.5 rounded-full text-xs font-medium bg-error text-error-foreground">
                      {species?.status}
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-[var(--color-foreground)] mb-2">{species?.name}</h3>
                    <p className="text-sm italic text-[var(--color-muted-foreground)] mb-4">{species?.scientificName}</p>
                    <p className="text-sm text-[var(--color-muted-foreground)] leading-relaxed">{species?.description}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Conservation Programs */}
        <section className="py-12 bg-[var(--color-background)]">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-foreground)] mb-4">
                Conservation Programs
              </h2>
              <p className="text-lg text-[var(--color-muted-foreground)] max-w-3xl mx-auto">
                Comprehensive initiatives protecting marine turtles from nest to ocean.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {conservationPrograms?.map((program, index) =>
              <div key={index} className="bg-[var(--color-card)] rounded-lg overflow-hidden shadow-conservation hover:shadow-conservation-lg animation-organic border border-[var(--color-border)]">
                  {/* Program Image */}
                  <div className="relative h-48 overflow-hidden">
                    <Image
                      src={program?.image}
                      alt={program?.imageAlt}
                      className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  </div>
                  
                  {/* Program Content */}
                  <div className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="bg-[var(--color-primary)]/10 p-3 rounded-lg">
                        <Icon name={program?.icon} size={24} className="text-[var(--color-primary)]" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-[var(--color-foreground)] mb-2">{program?.title}</h3>
                        <p className="text-sm text-[var(--color-muted-foreground)] leading-relaxed mb-3">{program?.description}</p>
                        <div className="bg-[var(--color-success)]/10 px-3 py-2 rounded-lg">
                          <p className="text-xs text-[var(--color-success)] font-medium">{program?.impact}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Threats Section */}
        <section className="py-12 bg-[var(--color-background)]">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-foreground)] mb-4">
                Conservation Threats
              </h2>
              <p className="text-lg text-[var(--color-muted-foreground)] max-w-3xl mx-auto">
                Major challenges facing marine turtle populations and our response strategies.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {threats?.map((threat, index) =>
              <div key={index} className="bg-[var(--color-card)] rounded-lg p-6 shadow-conservation border border-[var(--color-border)]">
                  <div className="bg-[var(--color-error)]/10 p-3 rounded-lg w-fit mb-4">
                    <Icon name={threat?.icon} size={24} className="text-[var(--color-error)]" />
                  </div>
                  <h3 className="text-base font-bold text-[var(--color-foreground)] mb-2">{threat?.title}</h3>
                  <p className="text-sm text-[var(--color-muted-foreground)]">{threat?.description}</p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Data Tables Section */}
        <section className="py-12 bg-[var(--color-background)]">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="space-y-8">
              <TurtleHatchlingTable />
              <TurtleNestingTable />
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-16 bg-gradient-to-b from-[var(--color-background)] to-[var(--color-primary)]/10">
          <div className="container mx-auto px-4 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-foreground)] mb-4">
              Support Marine Turtle Conservation
            </h2>
            <p className="text-lg text-[var(--color-muted-foreground)] max-w-2xl mx-auto mb-8">
              Join our volunteer programs, adopt a nest, or contribute to research efforts protecting these ancient mariners.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button size="lg" iconName="Heart">
                Become a Volunteer
              </Button>
              <Button variant="outline" size="lg" iconName="Info">
                Learn More
              </Button>
            </div>
          </div>
        </section>
      </div>
    </div>);

};

export default MarineTurtleConservation;